import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Section } from '@/shared/ui/layout/Section';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { Button } from '@/shared/ui/buttons/Button';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { CheckCircle2Icon } from '@/shared/ui/icons';
import { useToast } from '@/shared/hooks/useToast';
import { useMutate } from '@/shared/hooks/useMutate';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { useSplitDetail, useSetSplitTargets, apiSplitConfirmShipping } from '@/entities/splits';
import { useParcelDetail } from '@/entities/parcels';
import { useParcelProducts } from '@/entities/parcels-products';
import {
  FeaturesSplitsStatusChip,
  FeaturesSplitsTargetCard,
  FeaturesSplitsTargetsForm,
  splitTargetsFormSchema,
  buildEmptyTarget,
  mapTargetToFormValues,
  mapParcelToTargetFormValues,
  mapFormValuesToTargetsPayload,
  type SplitTargetsFormValues,
} from '@/features/splits';
import type { ApiParcelsProductsGetItem } from '@/shared/types/api/parcels-products';
import { formatUsd } from '@/shared/utils/currency';
import { formatDateTime } from '@/shared/utils/date';
import type { ApiParcelsGetItem } from '@/shared/types/api/parcels';
import type { ApiSplitsGetItem } from '@/shared/types/api/splits';

const Row = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
    <span className="text-ink-muted">{label}</span>
    <span className="text-right font-medium text-ink">{value || '—'}</span>
  </div>
);

// ---------------------------------------------------------------------------
// DRAFT editor — Task 1b / Task 7
// ---------------------------------------------------------------------------

// INNER form: owns ``useForm`` so it only mounts after the parcel is loaded and
// ``defaultValues`` are correct at mount. Seeds one target from the parcel's
// recipient ONLY when there are no saved targets — never overwrites saved ones.
const DraftTargetsForm = ({
  id,
  split,
  parcel,
  products,
}: {
  id: string;
  split: ApiSplitsGetItem;
  parcel: ApiParcelsGetItem | undefined;
  products: ApiParcelsProductsGetItem[];
}) => {
  const { t } = useTranslation('splits');
  const toast = useToast();
  const navigate = useNavigate();
  const setTargets = useSetSplitTargets(id);
  const form = useForm<SplitTargetsFormValues>({
    resolver: zodResolver(splitTargetsFormSchema),
    defaultValues: {
      targets: split.targets.length
        ? split.targets.map((tgt) => mapTargetToFormValues(tgt, products))
        : parcel
          ? [mapParcelToTargetFormValues(parcel, products)]
          : [buildEmptyTarget(products)],
    },
  });

  const handleSave = (values: SplitTargetsFormValues) =>
    setTargets.mutate(mapFormValuesToTargetsPayload(values), {
      onSuccess: () => {
        toast.success(t('toast.targetsSaved'));
        navigate(`/parcels/${split.parcel.id}`);
      },
      // Surface server-side 400s (e.g. distribution / duplicate package name).
      onError: (error) => toast.error(extractErrorMessage(error)),
    });

  return (
    <FeaturesSplitsTargetsForm
      form={form}
      onSubmit={handleSave}
      submitting={setTargets.isPending}
      products={products}
      parcelId={split.parcel.id}
    />
  );
};

// OUTER editor: fetches the parcel + its products and shows a spinner until both
// are loaded, so the inner form mounts with allocations correctly seeded.
const DraftTargetsEditor = ({ id, split }: { id: string; split: ApiSplitsGetItem }) => {
  const parcelQuery = useParcelDetail(split.parcel.id);
  const productsQuery = useParcelProducts(split.parcel.id);

  // When the user leaves the DRAFT editor (in-app back, browser back, or any
  // unmount) refresh the available-for-split list so an abandoned/unsaved draft's
  // parcel reappears immediately on return.
  const queryClient = useQueryClient();
  useEffect(
    () => () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.parcels.availableForSplit });
      void queryClient.invalidateQueries({ queryKey: queryKeys.parcels.all });
    },
    [queryClient],
  );

  if (parcelQuery.isLoading || productsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <DraftTargetsForm
      id={id}
      split={split}
      parcel={parcelQuery.data}
      products={productsQuery.data ?? []}
    />
  );
};

// ---------------------------------------------------------------------------
// Read-only details view — Tasks 5 & 6
// ---------------------------------------------------------------------------

const SplitDetailsView = ({ split }: { split: ApiSplitsGetItem }) => {
  const { t } = useTranslation('splits');
  const toast = useToast();
  const productsQuery = useParcelProducts(split.parcel.id);
  const [confirmShip, setConfirmShip] = useState(false);

  const products = productsQuery.data ?? [];
  const productsTotal = products.reduce((s, p) => s + p.quantity * p.price_per_item, 0);

  const confirmShip$ = useMutate({
    // Dedicated cargo_id-aware endpoint: returns the serialized split (not a
    // {confirmed, skipped} summary) and works for admin-created, user-less
    // (cargo_id-owned) split parcels that /parcels/confirm-shipping rejects.
    api: () => apiSplitConfirmShipping(split.id),
    invalidateKeys: [
      queryKeys.splits.detail(split.id),
      queryKeys.splits.all,
      queryKeys.parcels.all,
      queryKeys.parcels.detail(split.parcel.id),
    ],
    onSuccess: () => {
      toast.success(t('parcels:confirmShipping.toast.success', { count: 1 }));
      setConfirmShip(false);
    },
    onError: (_e, message) => {
      toast.error(message);
      setConfirmShip(false);
    },
  });

  const isInProgress = split.status === 'IN_PROGRESS';

  return (
    <>
      <Section title={t('sectionProducts')}>
        {products.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('targetsEmpty')}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {products.map((p) => (
              <li key={p.id} className="flex items-center justify-between py-2 text-sm">
                <span className="min-w-0 flex-1 truncate text-ink-soft">
                  {p.full_name} <span className="text-ink-muted">× {p.quantity}</span>
                </span>
                <span className="font-medium text-ink">
                  {formatUsd(p.quantity * p.price_per_item)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-sm">
          <span className="text-ink-muted">{t('common:total')}</span>
          <span className="text-base font-semibold text-ink">{formatUsd(productsTotal)}</span>
        </div>
      </Section>

      <Section title={t('sectionTargetsCount', { count: split.targets.length })}>
        {split.targets.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('targetsEmpty')}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {split.targets.map((target) => (
              <FeaturesSplitsTargetCard key={target.id} target={target} />
            ))}
          </div>
        )}
      </Section>

      {isInProgress && (
        <Button
          onClick={() => setConfirmShip(true)}
          loading={confirmShip$.isPending}
          leftIcon={<CheckCircle2Icon className="h-4 w-4" />}
          block
        >
          {t('parcels:confirmShipping.action')}
        </Button>
      )}

      <ConfirmDialog
        open={confirmShip}
        title={t('parcels:confirmShipping.confirmTitle')}
        description={t('parcels:confirmShipping.confirmDescription')}
        confirmLabel={t('parcels:confirmShipping.confirmAction')}
        loading={confirmShip$.isPending}
        onCancel={() => {
          if (!confirmShip$.isPending) setConfirmShip(false);
        }}
        onConfirm={() => confirmShip$.mutate(undefined)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Parent — keeps a STABLE hook count (useTranslation/useParams/useSplitDetail
// only). All data hooks live in the child subtrees so the parent never crashes
// when the status flips DRAFT -> IN_PROGRESS and a different subtree mounts.
// ---------------------------------------------------------------------------
export const WidgetsSplitDetailMain = () => {
  const { t } = useTranslation('splits');
  const { id = '' } = useParams<{ id: string }>();
  const { data: split, isLoading, error, refetch } = useSplitDetail(id);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (error || !split) {
    return (
      <div className="p-4">
        <ErrorState
          message={extractErrorMessage(error, t('notFound'))}
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  const isDraft = split.status === 'DRAFT';

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('trek', { trekId: split.parcel.trek_id })}
        showBack
        backTo={`/parcels/${split.parcel.id}`}
      />
      <div className="flex flex-col gap-3 p-4 lg:gap-4 lg:p-6">
        <Section>
          <div className="flex items-center justify-between gap-2">
            <FeaturesSplitsStatusChip status={split.status} />
            <span className="text-xs text-ink-muted">{formatDateTime(split.created_at)}</span>
          </div>
          <div className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <Row label={t('parcelTotal')} value={formatUsd(split.parcel_total)} />
            <Row label={t('createdAt')} value={formatDateTime(split.created_at)} />
          </div>
        </Section>

        {isDraft ? (
          <DraftTargetsEditor id={id} split={split} />
        ) : (
          <SplitDetailsView split={split} />
        )}
      </div>
    </>
  );
};
